<template>
    <div>
        <!-- Başlık -->
        <b-card no-body class="mb-1">
            <div class="d-flex flex-wrap align-items-center justify-content-between p-1">
                <div>
                    <h4 class="mb-25 font-weight-bolder">Özel Kontrol Stok Raporu</h4>
                    <small class="text-muted">
                        {{ monthLabel }} ayı faturaları — sorumlu olduğunuz carilerin özel ürünlerden alım durumu.
                    </small>
                </div>
                <div class="mt-1 mt-md-0 d-flex align-items-center">
                    <button class="btn btn-flat-secondary btn-sm mr-1" v-on:click="toggleAll">
                        <feather-icon :icon="allExpanded ? 'ChevronsUpIcon' : 'ChevronsDownIcon'" class="mr-25"></feather-icon>
                        {{ allExpanded ? 'Hepsini Kapat' : 'Hepsini Aç' }}
                    </button>
                    <v-executer :loading="loading" :action="getData" text="Yenile"></v-executer>
                </div>
            </div>
        </b-card>

        <!-- Özet uyarı -->
        <b-alert variant="danger" :show="!loading && missingCariCount > 0" class="mb-1">
            <div class="alert-body d-flex align-items-center">
                <span class="material-icons-round mr-1">report</span>
                <div>
                    <strong>{{ missingCariCount }}</strong> cari bu ay özel ürünlerin tamamından alım yapmadı.
                    Toplam <strong>{{ items.length }}</strong> cari listeleniyor.
                </div>
            </div>
        </b-alert>
        <b-alert variant="success" :show="!loading && items.length > 0 && missingCariCount === 0" class="mb-1">
            <div class="alert-body d-flex align-items-center">
                <span class="material-icons-round mr-1">check_circle</span>
                <div>Tüm carileriniz bu ay özel ürünlerin tamamından alım yaptı.</div>
            </div>
        </b-alert>

        <!-- Liste -->
        <b-card no-body>
            <b-overlay :show="loading" opacity="0.2" rounded="sm">
                <div class="p-1">
                    <div v-if="!loading && sortedItems.length === 0" class="text-center text-muted p-2">
                        Bu ay için listelenecek cari bulunamadı.
                    </div>

                    <div v-for="cari in sortedItems" :key="cari.CariKod" class="scsr-row border rounded mb-75">
                        <!-- Cari başlık satırı -->
                        <div class="scsr-head d-flex align-items-center p-75 c-pointer"
                             v-on:click="toggle(cari.CariKod)">
                            <feather-icon
                                :icon="isExpanded(cari.CariKod) ? 'ChevronDownIcon' : 'ChevronRightIcon'"
                                class="mr-50 text-muted"></feather-icon>
                            <div class="flex-grow-1">
                                <span class="font-weight-bolder">{{ cari.CariKod }}</span>
                                <span class="text-muted"> — {{ cari.CariIsim || '-' }}</span>
                            </div>
                            <b-badge :variant="cari.HasMissing ? 'light-danger' : 'light-success'" class="scsr-badge">
                                {{ cari.PurchasedCount }} / {{ cari.TotalCount }} özel kalem
                            </b-badge>
                        </div>

                        <!-- Detay: ürün bazlı alındı/alınmadı -->
                        <b-collapse :visible="isExpanded(cari.CariKod)">
                            <div class="border-top">
                                <div v-for="item in cari.Items" :key="item.StockCode"
                                     class="d-flex align-items-center px-1 py-50 scsr-item">
                                    <span class="material-icons-round mr-1"
                                          :class="item.Purchased ? 'text-success' : 'text-danger'">
                                        {{ item.Purchased ? 'check_circle' : 'cancel' }}
                                    </span>
                                    <div class="flex-grow-1">
                                        <div>{{ item.StockName || '-' }}</div>
                                        <small class="text-muted">{{ item.StockCode }}</small>
                                    </div>
                                    <b-badge :variant="item.Purchased ? 'light-success' : 'light-danger'">
                                        {{ item.Purchased ? 'Alındı' : 'Alınmadı' }}
                                    </b-badge>
                                </div>
                                <div v-if="!cari.Items || cari.Items.length === 0" class="text-muted px-1 py-50">
                                    Tanımlı özel kontrol stoğu yok.
                                </div>
                            </div>
                        </b-collapse>
                    </div>
                </div>
            </b-overlay>
        </b-card>
    </div>
</template>

<script>
import { BCard, BAlert, BBadge, BCollapse, BOverlay } from 'bootstrap-vue'
import { ReportApi } from '@/api/report'

const TR_MONTHS = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']

export default {
    components: {
        BCard,
        BAlert,
        BBadge,
        BCollapse,
        BOverlay
    },
    data(){
        return {
            loading: false,
            items: [],
            expanded: {}
        }
    },
    computed: {
        monthLabel(){
            const d = new Date()
            return `${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}`
        },
        sortedItems(){
            // Eksik alım yapan cariler üstte, sonra cari koduna göre.
            return [...this.items].sort((a, b) => {
                if(a.HasMissing !== b.HasMissing) return a.HasMissing ? -1 : 1
                return (a.CariKod || '').localeCompare(b.CariKod || '', 'tr')
            })
        },
        missingCariCount(){
            return this.items.filter(c => c.HasMissing).length
        },
        allExpanded(){
            return this.items.length > 0 && this.items.every(c => this.expanded[c.CariKod])
        }
    },
    mounted(){
        this.getData()
    },
    methods: {
        getData(){
            this.loading = true
            return ReportApi.SpecialControlStockReport().then(res => {
                this.items = (res.data && res.data.Data) ? res.data.Data : []
            }).catch(err => {
                this.$result(null, err)
            }).finally(() => {
                this.loading = false
            })
        },
        isExpanded(cariKod){
            return !!this.expanded[cariKod]
        },
        toggle(cariKod){
            this.$set(this.expanded, cariKod, !this.expanded[cariKod])
        },
        toggleAll(){
            const target = !this.allExpanded
            const next = {}
            this.items.forEach(c => { next[c.CariKod] = target })
            this.expanded = next
        }
    }
}
</script>

<style scoped>
.c-pointer{ cursor: pointer; }
.scsr-head:hover{ background-color: rgba(115,103,240,0.06); }
.scsr-item + .scsr-item{ border-top: 1px solid rgba(0,0,0,0.04); }
.scsr-badge{ font-size: 0.75rem; }
.dark-layout .scsr-head:hover{ background-color: rgba(115,103,240,0.15); }
.dark-layout .scsr-item + .scsr-item{ border-top-color: rgba(255,255,255,0.06); }
</style>
